type ScrollLockKey = string;

// Module-level lock registry (client-side only).
const activeLocks = new Set<ScrollLockKey>();
let previousOverflow: string | null = null;
const lockDebug =
  typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production";
const lockStacks: Map<ScrollLockKey, string> | null = lockDebug ? new Map() : null;

function getBody(): HTMLBodyElement | null {
  if (typeof document === "undefined") return null;
  // In some TS DOM lib configurations, `document.body` is typed as `HTMLElement`.
  return document.body as HTMLBodyElement;
}

/**
 * Lock body scroll using a ref-counted key-based registry.
 *
 * This prevents "last writer wins" bugs where multiple components toggle
 * `document.body.style.overflow` and restore stale values in the wrong order.
 */
export function lockBodyScroll(key: ScrollLockKey): void {
  const body = getBody();
  if (!body) return;

  if (lockStacks && !lockStacks.has(key)) {
    lockStacks.set(key, new Error(`Body scroll lock acquired: ${key}`).stack ?? "");
  }

  if (activeLocks.size === 0) {
    previousOverflow = body.style.overflow;
    body.style.overflow = "hidden";
  }

  activeLocks.add(key);
}

/**
 * Unlock body scroll for a previously-acquired key.
 * When the last lock is released, restores the prior overflow value.
 */
export function unlockBodyScroll(key: ScrollLockKey): void {
  const body = getBody();
  if (!body) return;

  if (lockStacks && !activeLocks.has(key)) {
    // Helps catch mismatched unlocks in development.
    console.warn(`[body-scroll-lock] unlock called for non-existent key: ${key}`);
  }

  activeLocks.delete(key);
  if (lockStacks) lockStacks.delete(key);

  if (activeLocks.size === 0) {
    body.style.overflow = previousOverflow ?? "";
    previousOverflow = null;
  }
}

// Debug/testing helper (not for app logic).
export function getBodyScrollLockCount(): number {
  return activeLocks.size;
}

// Debug helper (not for app logic).
export function getBodyScrollLockDebug(): Array<{ key: string; stack?: string }> {
  if (!lockStacks) return [];
  return Array.from(lockStacks.entries()).map(([key, stack]) => ({ key, stack }));
}
